import { PopularPartsTable } from "@/components/brand/PopularPartsTable";
import type { PopularPartRow } from "@/lib/brand-popular-parts";
import { ProductsRoutePending } from "@/components/catalogue/CatalogueRouteSkeletons";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { canonicalBrand } from "@/lib/brand-presentation";
import { ProductCard } from "@/components/ProductCard";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { BrandShoppingNav } from "@/components/brand/BrandShoppingNav";
import { BrandModelLinks } from "@/components/brand/BrandModelLinks";
import { getBrandBundle } from "@/lib/catalog.functions";

import { useT, pickLocalized } from "@/i18n/LanguageProvider";
import { fmtCurrencyBDT } from "@/i18n/format";

import { track } from "@/lib/analytics-client";
import { ShieldCheck, Wallet, RefreshCw, ChevronRight, Star } from "lucide-react";
import { hreflangLinks, ogImageMeta, ogLocaleMeta, SITE_URL } from "@/lib/seo";
import { MODEL_LANDINGS } from "@/data/model-landing";
import { brandCategoryHref, BRAND_CATEGORIES } from "@/data/brand-categories";
import { PopularGuidesStrip } from "@/components/PopularGuidesStrip";
import { guidesForCategory } from "@/lib/guides-for-category";
import { bnSafe } from "@/lib/typography";
import {
  findBrandSeed,
  brandPageTitle,
  brandPageDescription,
  brandScopeBn,
  brandJsonLd,
} from "@/lib/brand-seo";
import ogBrandImg from "@/assets/og/og-brand.jpg";
import { AnswerBlock } from "@/components/seo/AnswerBlock";
import { toBnDigits } from "@/lib/format-bn";
import { formatFreeDeliveryThreshold } from "@/lib/free-delivery";

const DISPLAY = "font-[family-name:var(--font-display)]";

export const Route = createFileRoute("/brand/$slug/")({
  pendingComponent: ProductsRoutePending,
  loader: async ({ params, context }) => {
    const data = await context.queryClient.fetchQuery({
      queryKey: ["brand", params.slug],
      queryFn: () => getBrandBundle({ data: { slug: params.slug, limit: 24 } }),
    });

    if (!data) throw notFound();
    return data;
  },
  head: ({ loaderData, params, matches }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Brand not found — Jantro Bazar" }, { name: "robots", content: "noindex" }],
      };
    }
    const name = loaderData.brand.name;
    const count = loaderData.product_count;
    const seed = findBrandSeed(params.slug);
    // Model names come from the same seed the page renders as "popular
    // models", so the snippet promises exactly what the page delivers.
    // Only name models of the brand's own vehicle type ("Honda … Vezel
    // motorcycle" mixed a car into a bike snippet).
    const modelNames = MODEL_LANDINGS.filter(
      (m) =>
        m.brand_slug === params.slug &&
        (seed?.type !== "motorcycle" && seed?.type !== "car" ? true : m.vehicle_type === seed.type),
    ).map(
      (m) => m.model,
    );
    const title = brandPageTitle(name, seed?.type, modelNames, seed?.name_bn);
    const desc = brandPageDescription(
      name,
      seed?.type,
      count,
      (loaderData as any).price_min && (loaderData as any).price_max
        ? { min: (loaderData as any).price_min, max: (loaderData as any).price_max }
        : null,
      modelNames,
      seed?.name_bn,
    );
    const url = `https://jantrobazar.com/brand/${params.slug}`;
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://jantrobazar.com/" },
        {
          "@type": "ListItem",
          position: 2,
          name: "Brands",
          item: "https://jantrobazar.com/brands",
        },
        { "@type": "ListItem", position: 3, name, item: url },
      ],
    };
    const brandLd = brandJsonLd({
      name,
      url,
      description: desc,
      seed,
      aggregateRating:
        loaderData.avg_rating > 0
          ? {
              ratingValue: loaderData.avg_rating,
              reviewCount: loaderData.products.filter((p: any) => p.review_count > 0).length,
            }
          : undefined,
      priceRange:
        (loaderData as any).price_min && (loaderData as any).price_max
          ? {
              min: (loaderData as any).price_min,
              max: (loaderData as any).price_max,
              offerCount: count,
            }
          : undefined,
    });

    const stockedParts = (((loaderData as any).popular_parts ?? []) as PopularPartRow[]).filter(
      (r) => r.min_price != null,
    );
    const popularLd = stockedParts.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `Popular ${name} parts and prices`,
          itemListElement: stockedParts.map((r, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Product",
              name: `${name} ${r.label_en}`,
              brand: { "@type": "Brand", name },
              offers: {
                "@type": "AggregateOffer",
                priceCurrency: "BDT",
                lowPrice: r.min_price,
                offerCount: r.listings,
              },
            },
          })),
        }
      : null;
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `${name} parts — Jantro Bazar`,
      numberOfItems: loaderData.products.length,
      itemListElement: loaderData.products.slice(0, 20).map((p: any, i: number) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://jantrobazar.com/products/${p.slug}`,
        name: p.title_en,
      })),
    };
    const ogImgUrl = `${SITE_URL}${ogBrandImg}`;
    const isLeaf = matches[matches.length - 1]?.routeId === "/brand/$slug/";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        // A seeded brand with zero live listings still renders a useful hub,
        // but we never ask Google to index an empty shelf.
        ...(count === 0 ? [{ name: "robots", content: "noindex, follow" }] : []),

        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: "Jantro Bazar" },
        ...ogImageMeta(ogImgUrl, `${name} — ${brandScopeBn(seed?.type)} · Jantro Bazar`),
        ...ogLocaleMeta(),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
      ],
      // `/brand/$slug` is also the parent of `/brand/$slug/$category`, and
      // TanStack concatenates `links` across matches — emit canonical +
      // hreflang only when this route is the leaf, or the child page would
      // ship two canonicals.
      links: isLeaf ? [{ rel: "canonical", href: url }, ...hreflangLinks(url)] : [],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
        { type: "application/ld+json", children: JSON.stringify(brandLd) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
        ...(popularLd ? [{ type: "application/ld+json", children: JSON.stringify(popularLd) }] : []),
      ],
    };
  },
  errorComponent: () => <div className="p-16 text-center">Failed to load.</div>,
  notFoundComponent: () => <div className="p-16 text-center">Brand not found.</div>,
  component: BrandPage,
});

type SortKey = "popular" | "newest" | "price_asc" | "price_desc";

function BrandPage() {
  const data = Route.useLoaderData();
  const { lang, t } = useT();
  const { brand, products, categories_in_brand, related_brands, product_count, avg_rating } = data;
  const priceMin = (data as any).price_min as number | null;
  const priceMax = (data as any).price_max as number | null;

  const pageSize = (data as any).page_size ?? 24;
  const seededCats = BRAND_CATEGORIES.find((b) => b.brand_slug === brand.slug)?.categories ?? [];

  const fetchBundle = useServerFn(getBrandBundle);
  const [sort, setSort] = useState<SortKey>("popular");
  const [attemptedSort, setAttemptedSort] = useState<SortKey>("popular");
  const [replaced, setReplaced] = useState<any[] | null>(null);
  const [extra, setExtra] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [requestError, setRequestError] = useState<"sort" | "more" | null>(null);
  const identity = canonicalBrand(brand.slug);
  const displayName = lang === "bn" ? identity?.name_bn ?? brand.name_bn ?? brand.name : brand.name;
  const shown = [...(replaced ?? products), ...extra];
  const hasMore = shown.length > 0 && shown.length < product_count;

  async function changeSort(next: SortKey) {
    if (loading) return;
    setLoading(true);
    setRequestError(null);
    setAttemptedSort(next);
    try {
      const r: any = await fetchBundle({
        data: { slug: brand.slug, sort: next, limit: pageSize },
      });
      if (!r) throw new Error("Unavailable");
      setSort(next);
      setReplaced(r.products ?? []);
      setExtra([]);
    } catch {
      setRequestError("sort");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (loading) return;
    setLoading(true);
    setRequestError(null);
    try {
      const r: any = await fetchBundle({
        data: { slug: brand.slug, sort, limit: pageSize, offset: shown.length },
      });
      if (!r) throw new Error("Unavailable");
      setExtra((e) => [...e, ...((r?.products ?? []) as any[])]);
    } catch {
      setRequestError("more");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    track("view_brand", { brand_slug: brand.slug, brand_name: brand.name });
  }, [brand.slug, brand.name]);

  return (
    <div>
      <main className="flex-1 bg-background text-foreground">
        <div className="container-app py-5 md:py-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs md:text-sm text-foreground/50">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary transition">
                  {lang === "bn" ? "হোম" : "Home"}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline text-foreground/30" />
              </li>
              <li>
                <Link to="/brands" className="hover:text-primary transition">
                  {lang === "bn" ? "ব্র্যান্ড" : "Brands"}
                </Link>
              </li>
              <li aria-hidden>
                <ChevronRight className="h-3 w-3 inline text-foreground/30" />
              </li>
              <li aria-current="page" className="text-foreground font-semibold">{displayName}</li>
            </ol>
          </nav>

          <header className="mt-5 flex flex-wrap items-center gap-4 border-b border-border pb-5">
            <BrandLogo name={brand.name} domain={identity?.logoDomain ?? brand.logo_domain ?? ""} size={64} eager />
            <div className="min-w-0 flex-1"><h1 className="font-display text-3xl font-bold leading-tight md:text-4xl">{displayName}</h1>{lang === "bn" && <p className="mt-1 text-sm text-muted-foreground">{brand.name}</p>}<p className="mt-2 text-sm text-muted-foreground">{product_count.toLocaleString(lang === "bn" ? "bn" : "en")} {lang === "bn" ? "টি পার্টস" : "parts listed"}{priceMin && priceMax ? ` · ${fmtCurrencyBDT(priceMin, lang)} – ${fmtCurrencyBDT(priceMax, lang)}` : ""}{avg_rating > 0 ? ` · ★ ${avg_rating.toFixed(1)}` : ""}</p>{(() => { const ms = MODEL_LANDINGS.filter((m) => m.brand_slug === brand.slug).slice(0, 3).map((m) => m.model); return ms.length > 0 ? <p className="mt-2 text-sm text-foreground/80">{lang === "bn" ? `${ms.join(", ")} সহ সব মডেলের পার্টস। যা নেই, অনুরোধ করুন — বিক্রেতারা দাম জানাবেন।` : `Parts for ${ms.join(", ")} and more. Can't find it? Ask — sellers reply with a price.`}</p> : null; })()}</div>
            <div className="flex flex-wrap gap-2"><Button asChild className="min-h-11"><Link to="/request-a-part">{lang === "bn" ? "দাম জানতে পার্টের অনুরোধ করুন" : "Request a part price"}</Link></Button><Button asChild variant="outline" className="min-h-11"><Link to="/brands">{lang === "bn" ? "সব ব্র্যান্ড" : "All brands"}</Link></Button></div>
          </header>
          <BrandModelLinks brandName={brand.name} domain={identity?.logoDomain ?? brand.logo_domain ?? ""} />

          {/* Product grid */}
          <section className="mt-8 grid gap-6 lg:grid-cols-[15rem_minmax(0,1fr)] lg:items-start">
            <BrandShoppingNav
              brandSlug={brand.slug}
              brandName={lang === "bn" && brand.name_bn ? brand.name_bn : brand.name}
              categories={seededCats}
              lang={lang === "bn" ? "bn" : "en"}
            />
            <div className="min-w-0">
            <div className="flex flex-wrap items-end justify-between mb-4 gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-extrabold  mb-2">
                  <span className="text-primary">{lang === "bn" ? "পার্টস" : "PARTS"}</span>
                  
                </div>
                <h2
                  className={`${DISPLAY} text-2xl md:text-3xl font-extrabold ${lang === "bn" ? "" : ""} text-foreground`}
                >
                  {lang === "bn" ? `${displayName} পার্টস` : `All ${brand.name} listings`}
                </h2>
                <div
                  className="h-[3px] w-12 bg-primary rounded-full mt-3"
                  aria-hidden
                />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <label className="sr-only" htmlFor="brand-sort">
                  {lang === "bn" ? "সাজান" : "Sort"}
                </label>
                {shown.length > 0 && (
                  <select
                    id="brand-sort"
                    disabled={loading}
                    value={sort}
                    onChange={(e) => changeSort(e.target.value as SortKey)}
                    className="h-11 rounded-md border border-border/15 bg-card px-3 text-xs font-semibold text-foreground/80"
                  >
                    <option value="popular">{lang === "bn" ? "জনপ্রিয়" : "Popular"}</option>
                    <option value="newest">{lang === "bn" ? "নতুন" : "Newest"}</option>
                    <option value="price_asc">
                      {lang === "bn" ? "দাম: কম → বেশি" : "Price: low → high"}
                    </option>
                    <option value="price_desc">
                      {lang === "bn" ? "দাম: বেশি → কম" : "Price: high → low"}
                    </option>
                  </select>
                )}
                <span className="text-xs text-foreground/50 font-semibold tabular-nums">
                  {product_count} {lang === "bn" ? "টি পার্টস" : "items"}
                </span>
              </div>
            </div>
            {requestError && <div role="alert" className="mb-4 flex flex-wrap items-center gap-3 text-sm text-destructive"><span>{lang === "bn" ? "পার্টস লোড হয়নি। আবার চেষ্টা করুন।" : "Could not load parts. Please try again."}</span><Button variant="outline" disabled={loading} onClick={() => requestError === "more" ? loadMore() : changeSort(attemptedSort)}>{lang === "bn" ? "আবার চেষ্টা" : "Retry"}</Button></div>}
            <div aria-busy={loading}>
            {shown.length > 0 ? (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                  {shown.map((p: any) => (
                    <ProductCard key={p.id} p={p} />
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                  {hasMore && (
                    <Button
                      type="button"
                      onClick={loadMore}
                      disabled={loading}
                      className="inline-flex items-center h-11 rounded-md bg-primary px-6 text-sm font-semibold text-primary-foreground disabled:opacity-60"
                    >
                      {loading
                        ? lang === "bn"
                          ? "লোড হচ্ছে…"
                          : "Loading…"
                        : lang === "bn"
                          ? "আরও দেখুন"
                          : "Load more"}
                    </Button>
                  )}
                  <Link
                    to="/products"
                    search={{ brand: brand.name } as never}
                    className="inline-flex items-center h-11 rounded-md border border-border px-5 text-sm font-semibold text-foreground/80 hover:text-foreground transition"
                  >
                    {lang === "bn" ? "ক্যাটালগে সব দেখুন" : "See all in catalogue"}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border/15 px-6 py-12 text-center">
                <p className="text-foreground/70 text-sm md:text-base">
                  {lang === "bn"
                    ? `এই মুহূর্তে ${brand.name} পার্টস লিস্টেড নেই — নিচের ক্যাটাগরি দেখুন বা কোট চান।`
                    : `No ${brand.name} parts are listed right now — browse the categories below or request a quote.`}
                </p>
                {seededCats.length > 0 && (
                  <div className="mt-6 flex flex-wrap justify-center gap-2">
                    {seededCats.map((c) => (
                      <Link
                        key={c.slug}
                        to="/brand/$slug/$category"
                        params={{ slug: brand.slug, category: c.slug }}
                        className="inline-flex items-center min-h-11 rounded-full border border-border/10 bg-card px-4 text-xs font-semibold text-foreground/80 hover:border-[color:var(--color-ember)]/60 hover:text-foreground transition"
                      >
                        {lang === "bn" ? c.label_bn : c.label_en}
                      </Link>
                    ))}
                  </div>
                )}
                <div className="mt-6 flex flex-wrap justify-center gap-3">
                  <a
                    href={`https://wa.me/8801811600591?text=${encodeURIComponent(`${brand.name} parts`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center h-11 rounded-md bg-primary px-5 text-sm font-semibold text-primary-foreground"
                  >
                    {lang === "bn" ? "কোট চান" : "Request a quote"}
                  </a>
                  <Link
                    to="/products"
                    className="inline-flex items-center h-11 rounded-md border border-border/15 px-5 text-sm font-semibold text-foreground/80 hover:text-foreground transition"
                  >
                    {lang === "bn" ? "সব পার্টস দেখুন" : "Browse all parts"}
                  </Link>
                </div>
              </div>
            )}
            </div>
            </div>
          </section>

          {/* Quick answer — same live numbers that back the AggregateOffer. */}
          {priceMin && priceMax ? (
            <AnswerBlock
              question={
                lang === "bn"
                  ? `বাংলাদেশে ${brand.name} পার্টসের দাম কত?`
                  : `How much do ${brand.name} parts cost in Bangladesh?`
              }
              answer={
                lang === "bn"
                  ? `যন্ত্র বাজারে ${brand.name} পার্টস ${fmtCurrencyBDT(priceMin, "bn")} থেকে ${fmtCurrencyBDT(priceMax, "bn")} দামে পাওয়া যায় — ${toBnDigits(String(product_count))}টি লিস্টিং, ${toBnDigits(String(categories_in_brand.length))}টি ক্যাটাগরিতে। সব দাম টাকায়, সারাদেশে ক্যাশ অন ডেলিভারি এবং ৳${formatFreeDeliveryThreshold("bn")} টাকার বেশি অর্ডারে ফ্রি ডেলিভারি।`
                  : `${brand.name} parts on Jantro Bazar range from ${fmtCurrencyBDT(priceMin, "en")} to ${fmtCurrencyBDT(priceMax, "en")} across ${product_count} listing${product_count === 1 ? "" : "s"} in ${categories_in_brand.length} categor${categories_in_brand.length === 1 ? "y" : "ies"}. Prices are in Bangladeshi Taka, cash on delivery is available nationwide, and orders above ৳${formatFreeDeliveryThreshold("en")} ship free.`
              }
              facts={[
                {
                  label: lang === "bn" ? "দামের রেঞ্জ" : "Price range",
                  value: `${fmtCurrencyBDT(priceMin, lang)} – ${fmtCurrencyBDT(priceMax, lang)}`,
                },
                {
                  label: lang === "bn" ? "লিস্টিং" : "Listings",
                  value:
                    lang === "bn" ? toBnDigits(String(product_count)) : String(product_count),
                },
                {
                  label: lang === "bn" ? "ডেলিভারি" : "Delivery",
                  value: lang === "bn" ? "১–৫ দিন, সারাদেশে" : "1–5 days, nationwide",
                },
                {
                  label: lang === "bn" ? "পেমেন্ট ও রিটার্ন" : "Payment & returns",
                  value: lang === "bn" ? "ক্যাশ অন ডেলিভারি · ৭ দিন" : "Cash on delivery · 7 days",
                },
              ]}
              footnote={
                lang === "bn"
                  ? "দাম লাইভ লিস্টিং থেকে নেওয়া এবং বিক্রেতা ও কন্ডিশনভেদে বদলাতে পারে।"
                  : "Range comes from live listings and varies by seller and condition."
              }
            />
          ) : null}



          <PopularPartsTable
            rows={((data as any).popular_parts ?? []) as PopularPartRow[]}
            brandName={brand.name}
            brandNameBn={brand.name_bn}
            brandSlug={brand.slug}
            asOf={(data as any).prices_as_of ?? ""}
            lang={lang}
          />

          {/* Popular models for this brand (phase 142) */}
          {(() => {
            const models = MODEL_LANDINGS.filter((m) => m.brand_slug === brand.slug);
            if (models.length === 0) return null;
            return (
              <section className="mt-12">
                <div className="text-[11px] font-extrabold  mb-4">
                  <span className="text-primary">02</span>
                  <span className="text-foreground/40">
                    {" "}
                    / {lang === "bn" ? "জনপ্রিয় মডেল" : "POPULAR MODELS"}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {models.map((m) => (
                    <Link
                      key={m.slug}
                      to="/parts/$makeModel"
                      params={{ makeModel: m.slug }}
                      className="group rounded-md border border-border/10 bg-card p-4 hover:border-[color:var(--color-ember)]/60 transition"
                    >
                      <div
                        className={`text-[10px] font-bold text-primary mb-1 ${lang === "bn" ? "" : ""}`}
                      >
                        → {lang === "bn" ? "মডেল পার্টস" : "Model parts"}
                      </div>
                      <div
                        className={`${DISPLAY} text-base md:text-lg font-bold text-foreground leading-snug`}
                      >
                        {lang === "bn" ? `${m.make_bn} ${m.model_bn}` : `${m.make} ${m.model}`}
                      </div>
                      <div className="text-[11px] text-foreground/50 mt-1">{m.years_en}</div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })()}

          {/* Related brands */}
          {related_brands.length > 0 && (
            <section className="mt-14">
              <div className="text-[11px] font-extrabold  mb-4">
                <span className="text-primary">04</span>
                <span className="text-foreground/40">
                  {" "}
                  / {lang === "bn" ? "অন্যান্য ব্র্যান্ড" : "OTHER BRANDS"}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {related_brands.map((b: any) => (
                  <Link
                    key={b.slug}
                    to="/brand/$slug"
                    params={{ slug: b.slug }}
                    className="group flex items-center gap-3 rounded-md border border-border/10 bg-card p-4 hover:border-[color:var(--color-ember)]/60 transition"
                  >
                    <BrandLogo
                      name={canonicalBrand(b.slug)?.name ?? b.name}
                      domain={canonicalBrand(b.slug)?.logoDomain ?? b.logo_domain ?? ""}
                      size={32}
                      className="bg-[color:var(--color-ink-foreground)]/10"
                    />
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold  text-primary mb-0.5">
                        {lang === "bn" ? "আরও দেখুন" : "Browse"}
                      </div>
                      <div
                        className={`${DISPLAY} text-base md:text-lg font-bold text-foreground leading-snug break-words`}
                      >
                        {lang === "bn" && b.name_bn ? b.name_bn : b.name}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Buyer guides — light band, category-matched */}
        <div className="bg-background text-foreground">
          <div className="container-app py-12 md:py-16">
            <PopularGuidesStrip
              items={guidesForCategory(
                (categories_in_brand as any[]).map((c: any) => c.slug),
                6,
              )}
              headingEn={`Buyer guides — ${brand.name}`}
              headingBn={`ক্রেতা গাইড — ${brand.name}`}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

function Chip({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border/10 bg-card px-3 h-7 text-[11px] font-semibold  text-foreground/70">
      {icon}
      {label}
    </span>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div className="border-l border-border/10 pl-4">
      <dt
        className={`text-[11px] font-bold text-foreground/40 flex items-center gap-1 ${/[\u0980-\u09FF]/.test(label) ? "" : ""}`}
      >
        {label === "Avg. rating" || label === "গড় রেটিং" ? <Star className="h-3 w-3" /> : null}
        {label}
      </dt>
      <dd className={`${DISPLAY} text-2xl md:text-3xl font-extrabold text-foreground mt-1`}>{value}</dd>
    </div>
  );
}
